import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Key, Loader2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function ApiKeyEditDialog({
  editingApiKey, setEditingApiKey, apiKeyForm, setApiKeyForm,
  updateApiKeys, saving,
}) {
  return (
    <Dialog open={!!editingApiKey} onOpenChange={() => { setEditingApiKey(null); setApiKeyForm({}); }}>
      <DialogContent className="max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Update {editingApiKey === "wasabi" ? "Wasabi" : editingApiKey === "hubtel" ? "Hubtel" : "Brevo"} Credentials
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {editingApiKey === "wasabi" && (
            <>
              <div className="space-y-2">
                <Label>Access Key</Label>
                <Input value={apiKeyForm.access_key || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, access_key: e.target.value }))} placeholder="Enter new access key (leave blank to keep current)" className="rounded-xl font-mono" data-testid="wasabi-access-key" />
              </div>
              <div className="space-y-2">
                <Label>Secret Key</Label>
                <Input type="password" value={apiKeyForm.secret_key || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, secret_key: e.target.value }))} placeholder="Enter new secret key (leave blank to keep current)" className="rounded-xl font-mono" data-testid="wasabi-secret-key" />
              </div>
              <div className="space-y-2">
                <Label>Bucket Name</Label>
                <Input value={apiKeyForm.bucket || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, bucket: e.target.value }))} placeholder="e.g., greatmessiah-publications" className="rounded-xl font-mono" data-testid="wasabi-bucket" />
              </div>
              <div className="space-y-2">
                <Label>Endpoint URL</Label>
                <Input value={apiKeyForm.endpoint || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, endpoint: e.target.value }))} placeholder="e.g., https://s3.wasabisys.com" className="rounded-xl font-mono" data-testid="wasabi-endpoint" />
              </div>
            </>
          )}

          {editingApiKey === "hubtel" && (
            <>
              <div className="space-y-2">
                <Label>API ID</Label>
                <Input value={apiKeyForm.api_id || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, api_id: e.target.value }))} placeholder="Enter Hubtel API ID" className="rounded-xl font-mono" data-testid="hubtel-api-id" />
              </div>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input type="password" value={apiKeyForm.api_key || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, api_key: e.target.value }))} placeholder="Enter Hubtel API Key" className="rounded-xl font-mono" data-testid="hubtel-api-key" />
              </div>
              <div className="space-y-2">
                <Label>Merchant Account Number</Label>
                <Input value={apiKeyForm.merchant_account || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, merchant_account: e.target.value }))} placeholder="e.g., 2037928" className="rounded-xl font-mono" data-testid="hubtel-merchant" />
              </div>
            </>
          )}

          {editingApiKey === "brevo" && (
            <>
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input type="password" value={apiKeyForm.api_key || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, api_key: e.target.value }))} placeholder="Enter Brevo API Key" className="rounded-xl font-mono" data-testid="brevo-api-key" />
              </div>
              <div className="space-y-2">
                <Label>Sender Email <span className="text-red-500">*</span></Label>
                <Input type="email" value={apiKeyForm.sender_email || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, sender_email: e.target.value }))} placeholder="e.g., noreply@yourdomain.com" className="rounded-xl" data-testid="brevo-sender-email" />
                <p className="text-xs text-gray-500">Must be a verified sender in your Brevo account</p>
              </div>
              <div className="space-y-2">
                <Label>Sender Name</Label>
                <Input value={apiKeyForm.sender_name || ""} onChange={e => setApiKeyForm(prev => ({ ...prev, sender_name: e.target.value }))} placeholder="e.g., Great Messiah Publication" className="rounded-xl" data-testid="brevo-sender-name" />
              </div>
            </>
          )}

          <div className="bg-amber-50 rounded-xl p-3 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Only fill in fields you want to update. Leave fields blank to keep current values.
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setEditingApiKey(null); setApiKeyForm({}); }}>
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-xl"
              onClick={() => {
                const filteredCreds = Object.fromEntries(
                  Object.entries(apiKeyForm).filter(([_, v]) => v && v.trim())
                );
                if (Object.keys(filteredCreds).length === 0) {
                  toast.error("Please fill in at least one field");
                  return;
                }
                updateApiKeys(editingApiKey, filteredCreds);
              }}
              disabled={saving}
              data-testid="save-api-key"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
